import {
  DEFAULT_ELEVENLABS_AGENT_ID,
  DID_RANGE_END,
  DID_RANGE_START,
  DidInventoryItem,
  DidStatus,
  GOLDEN_REFERENCE_DID
} from "./types";

const ALLOWED_TRANSITIONS: Record<DidStatus, DidStatus[]> = {
  available: ["reserved", "assigned", "blocked"],
  reserved: ["available", "assigned", "blocked"],
  assigned: ["configured", "reserved", "blocked"],
  configured: ["testing", "assigned", "blocked"],
  testing: ["live", "configured", "blocked"],
  live: ["testing", "retired", "blocked"],
  retired: ["available", "blocked"],
  blocked: ["available", "reserved"]
};

export function normalizeDid(input: string): string {
  const did = input.trim();
  if (!/^0\d{8}$/.test(did)) {
    throw new Error("DID must be a 9-digit UAE national number starting with 0.");
  }
  if (!isDidInRange(did)) {
    throw new Error(`DID must be between ${DID_RANGE_START} and ${DID_RANGE_END}.`);
  }
  return did;
}

export function isDidInRange(input: string): boolean {
  if (!/^0\d{8}$/.test(input)) return false;
  const value = Number(input);
  return value >= Number(DID_RANGE_START) && value <= Number(DID_RANGE_END);
}

export function toE164(input: string): string {
  const did = normalizeDid(input);
  return `+971${did.slice(1)}`;
}

export function generateDidRange(): Array<{ did: string; e164: string }> {
  const start = Number(DID_RANGE_START);
  const end = Number(DID_RANGE_END);
  return Array.from({ length: end - start + 1 }, (_, index) => {
    const did = String(start + index).padStart(9, "0");
    return { did, e164: toE164(did) };
  });
}

export function seedDidItem(didInput: string, actor: string, now = new Date()): DidInventoryItem {
  const did = normalizeDid(didInput);
  const timestamp = now.toISOString();
  const isGolden = did === GOLDEN_REFERENCE_DID;

  return {
    did,
    e164: toE164(did),
    range_id: "meydan-0477776xx",
    extension_suffix: did.slice(-3),
    status: isGolden ? "live" : "available",
    protection: isGolden ? "golden_reference" : "normal",
    assigned_user: isGolden
      ? {
          name: "Meydan AI Operator",
          email: "ops@meydanai.com",
          notes: "Working golden reference DID. Do not mutate from the MVP console."
        }
      : null,
    assigned_agent_id: DEFAULT_ELEVENLABS_AGENT_ID,
    assistant_profile: {
      type: "meydan_operator",
      display_name: "Meydan AI - Operator",
      owner: "Meydan AI"
    },
    elevenlabs_phone_number_id: isGolden ? "phnum_9201kp2srahhfxea408t7wsbd859" : undefined,
    pbx_route_status: isGolden ? "verified" : "not_started",
    pbx_dial_pattern_status: isGolden ? "verified" : "manual_pending",
    elevenlabs_status: isGolden ? "verified" : "not_started",
    last_test_result: isGolden ? "two_way_audio_ok" : "not_tested",
    created_at: timestamp,
    updated_at: timestamp,
    updated_by: actor
  };
}

export function canTransitionStatus(from: DidStatus, to: DidStatus): boolean {
  if (from === to) return true;
  return ALLOWED_TRANSITIONS[from].includes(to);
}

export function assertCanTransitionStatus(from: DidStatus, to: DidStatus): void {
  if (!canTransitionStatus(from, to)) {
    throw new Error(`Cannot transition DID status from ${from} to ${to}.`);
  }
}

export function assertCanMarkLive(item: DidInventoryItem): void {
  if (item.pbx_route_status !== "verified") {
    throw new Error("PBX route must be verified before a DID can go live.");
  }
  if (item.pbx_dial_pattern_status !== "verified") {
    throw new Error("PBX dial pattern must be verified before a DID can go live.");
  }
  if (item.elevenlabs_status !== "verified") {
    throw new Error("ElevenLabs setup must be verified before a DID can go live.");
  }
  if (item.last_test_result !== "two_way_audio_ok") {
    throw new Error("Two-way audio test must pass before a DID can go live.");
  }
  if (!item.elevenlabs_phone_number_id) {
    throw new Error("ElevenLabs phone number ID is required before a DID can go live.");
  }
  if (!item.config_snapshot?.generated_at) {
    throw new Error("Generated config snapshot is required before a DID can go live.");
  }
}
